<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import http from '@/services/http';
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMutation } from '@tanstack/vue-query';
import { AxiosError } from 'axios';

interface RegisterFormErrors {
  name?: string[];
  email?: string[];
  password?: string[];
  password_confirmation?: string[];
  [key: string]: string[] | undefined;
}

interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
}

const router = useRouter();

const validationErrors = ref<RegisterFormErrors>({});
const name = ref('');
const email = ref('');
const password = ref('');
const password_confirmation = ref('');
const generalErrors = ref<string[]>([]);

const { mutate, isPending } = useMutation({
  mutationFn: async (formData: RegisterFormData) => {
    const response = await http.post('/api/register', formData);
    return response.data;
  },
  onSuccess: async () => {
    validationErrors.value = {};
    generalErrors.value = [];
    await router.push('/account/verify-email');
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError) {
      if (error.response?.status === 422) {
        validationErrors.value = error.response.data.errors;
        generalErrors.value = [];
      } else {
        validationErrors.value = {};
        generalErrors.value = Array.isArray(error.response?.data?.errors) 
          ? error.response.data.errors 
          : ['An unexpected error occurred. Please try again later.'];
      }
    } else {
      validationErrors.value = {};
      generalErrors.value = ['An unexpected error occurred. Please try again later.'];
    }
  }
});

const submitForm = async () => {
  validationErrors.value = {};
  generalErrors.value = [];
  mutate({
    name: name.value,
    email: email.value,
    password: password.value,
    password_confirmation: password_confirmation.value
  });
};
</script>

<template>
  <Card class="px-6 py-4">
    <form @submit.prevent="submitForm">
      <CardHeader class="pb-2">
        <CardTitle class="flex justify-end">
          <router-link
            v-slot="{ navigate }"
            custom 
            to="login"
          >
            <Button type="button" variant="link" @click="navigate">
              Already registered?
          </Button>
          </router-link>
        </CardTitle>
      </CardHeader>

      <CardContent class="space-y-4">
        <div v-if="generalErrors.length > 0" class="p-4 text-sm text-red-500 bg-red-50 rounded-md">
          <div v-for="(error, index) in generalErrors" :key="index">{{ error }}</div>
        </div>
        
        <div class="space-y-2">
          <InputLabel for="name" value="Name" />
          <Input
            id="name"
            type="text"
            class="block w-full"
            required
            autofocus
            autocomplete="name"
            name="name"
            v-model="name"
            :class="{ 'border-red-500': validationErrors['name'] }"
          />
          <InputError v-if="validationErrors['name']" :message="validationErrors['name'].join('. ')" class="mt-1" />
        </div>

        <div class="space-y-2">
          <InputLabel for="email" value="Email" />
          <Input
            id="email"
            type="email"
            class="block w-full"
            required
            autocomplete="username"
            name="email"
            v-model="email"
            :class="{ 'border-red-500': validationErrors['email'] }"
          />
          <InputError v-if="validationErrors['email']" :message="validationErrors['email'].join('. ')" class="mt-1" />
        </div>

        <div class="space-y-2">
          <InputLabel for="password" value="Password" />
          <Input
            id="password"
            type="password"
            class="block w-full"
            required
            autocomplete="new-password"
            name="password"
            v-model="password"
            :class="{ 'border-red-500': validationErrors['password'] }"
          />
          <InputError v-if="validationErrors['password']" :message="validationErrors['password'].join('. ')" class="mt-1" />
        </div>

        <div class="space-y-2">
          <InputLabel for="password_confirmation" value="Confirm Password" />
          <Input
            id="password_confirmation"
            type="password"
            class="block w-full"
            required
            autocomplete="new-password"
            name="password_confirmation"
            v-model="password_confirmation"
            :class="{ 'border-red-500': validationErrors['password_confirmation'] }"
          />
          <InputError
            v-if="validationErrors['password_confirmation']"
            :message="validationErrors['password_confirmation'].join('. ')"
            class="mt-1"
          />
        </div>
      </CardContent>

      <CardFooter class="flex justify-end items-center space-x-4 mt-8">
        <Button type="submit" :disabled="isPending">
          {{ isPending ? 'Registering...' : 'Register' }}
        </Button>
      </CardFooter>
    </form>
  </Card>
</template>